import { ratioOf } from './ratio';
import type { BarTone } from './ratio';

/**
 * Barre de progression — refonte « Nourri ».
 *
 * Le prototype en emploie quatre épaisseurs : 8 px pour les kcal d'un repas,
 * 5 px pour les macros du bandeau, 4 px pour celles d'une fiche repas, 11 px
 * pour la composition sous l'anneau. Même objet, même piste.
 *
 * ⚠️ `ratioOf` et `BarTone` vivent dans `ratio.ts` : `react-refresh` interdit
 * à un module de composants d'exporter autre chose.
 */

export function ProgressBar({
  value,
  max,
  tone = 'brand',
  size = 'md',
  label,
}: {
  value: number;
  max: number;
  tone?: BarTone;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Nom accessible. ⚠️ Une barre sans libellé visible à côté d'elle en a
   * besoin : `role="progressbar"` annonce une valeur, pas ce qu'elle mesure.
   */
  label?: string;
}) {
  const ratio = ratioOf(value, max);

  return (
    <div
      className={size === 'md' ? 'n-bar' : `n-bar n-bar--${size}`}
      role="progressbar"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className={`n-bar__fill n-bar__fill--${tone}`}
        style={{ width: `${ratio * 100}%` }}
      />
    </div>
  );
}

/**
 * Barre de composition — `design-tokens.md` la range parmi les composants
 * clés : « une barre segmentée sous l'anneau montrant la part protéines /
 * glucides / lipides dans les kcal consommées ».
 *
 * C'est la même piste que `ProgressBar`, avec plusieurs remplissages bout à
 * bout au lieu d'un seul — d'où le même CSS et ce fichier plutôt qu'un autre.
 *
 * ⚠️ **Les parts sont normalisées, pas affichées telles quelles.** Elles
 * décrivent une répartition : trois valeurs qui totalisent 0 laissent la piste
 * nue, ce qui est la bonne réponse à « rien n'a encore été mangé » — et non
 * trois segments à zéro, qui se peindraient quand même sur un pixel.
 */
export function CompositionBar({
  parts,
  size = 'lg',
  label,
}: {
  parts: { tone: BarTone; value: number }[];
  size?: 'xs' | 'sm' | 'md' | 'lg';
  label?: string;
}) {
  const total = parts.reduce((sum, p) => sum + Math.max(0, p.value), 0);

  return (
    <div
      className={size === 'md' ? 'n-bar' : `n-bar n-bar--${size}`}
      role="img"
      aria-label={label}
    >
      {total > 0 &&
        parts.map((p) => (
          <div
            key={p.tone}
            className={`n-bar__fill n-bar__fill--${p.tone}`}
            style={{ width: `${(Math.max(0, p.value) / total) * 100}%` }}
          />
        ))}
    </div>
  );
}
